import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import Raven from 'raven-js';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { useUpgradePoint } from 'ui-addon-upgrades/ums/useUpgradePoint';
import PortalIdParser from 'PortalIdParser';
import { isCustomCapability } from '../../editorCapabilities';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { DocumentsIcon } from 'foundations-assets/icons/components/DocumentsIcon';
import { InsertVideoIcon } from 'foundations-assets/icons/components/InsertVideoIcon';
import { InvoiceIcon } from 'foundations-assets/icons/components/InvoiceIcon';
import { LessonIcon } from 'foundations-assets/icons/components/LessonIcon';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { MeetingsIcon } from 'foundations-assets/icons/components/MeetingsIcon';
import { PaymentSubscriptionsIcon } from 'foundations-assets/icons/components/PaymentSubscriptionsIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { SalesTemplatesIcon } from 'foundations-assets/icons/components/SalesTemplatesIcon';
import { SignatureIcon } from 'foundations-assets/icons/components/SignatureIcon';
import { TextSnippetIcon } from 'foundations-assets/icons/components/TextSnippetIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Text } from 'foundations-components/components/Text';
import { VideoModalTypes } from '../../../draft-transplant/GoVideoDialog';
import { useHasHubSpotVideos, useHasMarketingVideoAccess, useHasMediaCaptureAccess, useHasOneToOneVideoAccess, useIsUngatedForHubSpotVideoCapture } from '../../commands/video/videoHooks';
import { useHasDocumentsPaidAccessScope } from '../../commands/documents/documentsHooks';
import { Link } from 'foundations-components/transitional/components/Link';
import { getDocumentsUrl, getVideosUrl } from '../../utils/manageLinks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getTypeText = (type, editorConfig) => {
  if (isCustomCapability(type) && editorConfig[type]) {
    return editorConfig[type].typeText;
  }
  switch (type) {
    case 'MEETINGS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.meetings"
      });
    case 'KNOWLEDGE_ARTICLE':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.knowledgeArticle"
      });
    case 'PAYMENT_LINK':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.paymentLink"
      });
    case 'INVOICES':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.invoices"
      });
    case 'VIDEO_INSERTION':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.video"
      });
    case 'SNIPPETS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.snippets"
      });
    case 'TEMPLATES':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.templates"
      });
    case 'DOCUMENTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.documents"
      });
    case 'QUOTES':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.quotes"
      });
    case 'SIGNATURE':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.signature"
      });
    default:
      Raven.captureMessage('NO_TYPE_TEXT');
      return '';
  }
};
const getTypeIcon = (type, editorConfig) => {
  if (isCustomCapability(type) && editorConfig[type]) {
    if (!editorConfig[type].typeIcon) {
      Raven.captureMessage('NO_TYPE_ICON');
      return LessonIcon;
    }
    return editorConfig[type].typeIcon;
  }
  switch (type) {
    case 'MEETINGS':
      return MeetingsIcon;
    case 'KNOWLEDGE_ARTICLE':
    case 'QUOTES':
      return LessonIcon;
    case 'PAYMENT_LINK':
      return PaymentSubscriptionsIcon;
    case 'INVOICES':
      return InvoiceIcon;
    case 'VIDEO_INSERTION':
      return InsertVideoIcon;
    case 'SNIPPETS':
      return TextSnippetIcon;
    case 'TEMPLATES':
      return SalesTemplatesIcon;
    case 'DOCUMENTS':
      return DocumentsIcon;
    case 'SIGNATURE':
      return SignatureIcon;
    default:
      Raven.captureMessage('NO_TYPE_ICON');
      return LessonIcon;
  }
};
const getDisabledText = (type, editorConfig) => {
  if (isCustomCapability(type) && editorConfig[type]) {
    return editorConfig[type].disabledText;
  }
  switch (type) {
    case 'MEETINGS':
      return undefined;
    case 'KNOWLEDGE_ARTICLE':
      return undefined;
    case 'PAYMENT_LINK':
      return undefined;
    case 'INVOICES':
      return undefined;
    case 'VIDEO_INSERTION':
      return undefined;
    case 'SNIPPETS':
    case 'SIGNATURE':
      return undefined;
    case 'TEMPLATES':
      return !editorConfig[type].contactEmail && editorConfig[type].outputType !== contentOutputs.EDITABLE ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.disabledTooltipMessage"
      }) : undefined;
    case 'DOCUMENTS':
      {
        var _requireContactEmail;
        const requireContactEmail = (_requireContactEmail = editorConfig[type].requireContactEmail) !== null && _requireContactEmail !== void 0 ? _requireContactEmail : true;
        const contactEmail = editorConfig[type].contactEmail;
        return requireContactEmail && !contactEmail ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "hubspot-prosemirror.insertMenu.disabledTooltipMessage"
        }) : undefined;
      }
    case 'QUOTES':
      return !editorConfig[type].contactVid ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.insertMenu.disabledTooltipMessage"
      }) : undefined;
    default:
      Raven.captureMessage('NO_DISABLED_TEXT');
      return undefined;
  }
};
const InsertHubSpotVideoButton = ({
  onSelectMediaType,
  onOpenModal,
  buttonLabel,
  disabled,
  disabledText,
  showNewTag,
  Icon,
  isInDropdown
}) => {
  const hasMarketingVideoAccess = useHasMarketingVideoAccess();
  const {
    openUpgradePage
  } = useUpgradePoint('crm-inbox-insert-hubspot-video', !hasMarketingVideoAccess);
  const {
    hasHubSpotVideos,
    loading
  } = useHasHubSpotVideos(disabled || !hasMarketingVideoAccess);
  const dataTestId = isInDropdown ? 'insert-video-hubspot-option' : 'insert-menu-VIDEO_INSERTION';
  if (!hasMarketingVideoAccess) {
    return /*#__PURE__*/_jsxs(Button, {
      "data-test-id": dataTestId,
      onClick: openUpgradePage,
      children: [Icon && /*#__PURE__*/_jsx(IconHolder, {
        marginInlineEnd: "300",
        icon: Icon,
        color: "icon-core-subtle"
      }), buttonLabel, /*#__PURE__*/_jsx(IconHolder, {
        marginInlineEnd: "300",
        icon: LockedIcon,
        color: "icon-core-subtle",
        marginInlineStart: "200",
        verticalAlign: "-0.25em"
      })]
    });
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !disabled && (loading || hasHubSpotVideos),
    title: disabledText || !hasHubSpotVideos && /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "hubspot-prosemirror.insertMenu.videoOptions.noVideosTooltip_jsx",
      elements: {
        Link
      },
      options: {
        href: getVideosUrl(PortalIdParser.get()),
        external: true
      }
    }),
    children: /*#__PURE__*/_jsx(Button, {
      "data-loading": loading,
      "data-test-id": dataTestId,
      disabled: disabled || loading || !hasHubSpotVideos,
      onClick: () => {
        onSelectMediaType('VIDEO_INSERTION');
        onOpenModal === null || onOpenModal === void 0 || onOpenModal(VideoModalTypes.INSERT_VIDEO_HUBSPOT);
      },
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [Icon && /*#__PURE__*/_jsx(IconHolder, {
          marginInlineEnd: "300",
          icon: Icon,
          color: "icon-core-subtle"
        }), buttonLabel, showNewTag && /*#__PURE__*/_jsx(Badge, {
          use: "new",
          marginInlineStart: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'hubspot-prosemirror.insertMenu.newTag'
          })
        }), !isInDropdown && /*#__PURE__*/_jsx(IconHolder, {
          icon: RightIcon
        })]
      })
    })
  });
};
export const InsertVideoDropdownButton = ({
  onSelectMediaType,
  onOpenModal,
  buttonLabel,
  disabled,
  disabledText,
  showNewTag,
  Icon,
  showCapture,
  showVidyard
}) => {
  const renderContent = () => /*#__PURE__*/_jsxs(List, {
    children: [showCapture && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsxs(Button, {
        "data-test-id": "insert-video-hubspot-capture-option",
        onClick: () => {
          onSelectMediaType('VIDEO_INSERTION');
          onOpenModal === null || onOpenModal === void 0 || onOpenModal(VideoModalTypes.INSERT_VIDEO_HUBSPOT_CAPTURE);
        },
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "hubspot-prosemirror.insertMenu.videoOptions.recordWithHubSpot"
        }), /*#__PURE__*/_jsx(Badge, {
          use: "new",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "hubspot-prosemirror.insertMenu.newTag"
          })
        })]
      })
    }, VideoModalTypes.INSERT_VIDEO_HUBSPOT_CAPTURE), showVidyard && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "insert-video-vidyard-option",
        onClick: () => {
          onSelectMediaType('VIDEO_INSERTION');
          onOpenModal === null || onOpenModal === void 0 || onOpenModal(VideoModalTypes.INSERT_VIDEO_VIDYARD);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "hubspot-prosemirror.insertMenu.videoOptions.recordWithVidyard"
        })
      })
    }, VideoModalTypes.INSERT_VIDEO_VIDYARD), /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(InsertHubSpotVideoButton, {
        buttonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "hubspot-prosemirror.insertMenu.videoOptions.selectFromHubSpot"
        }),
        disabled: false,
        showNewTag: false,
        onSelectMediaType: onSelectMediaType,
        onOpenModal: onOpenModal,
        isInDropdown: true
      })
    }, VideoModalTypes.INSERT_VIDEO_HUBSPOT)]
  });
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !disabled,
    title: disabledText,
    children: /*#__PURE__*/_jsx(Dropdown, {
      disabled: disabled,
      menuWidth: 260,
      popoverProps: {
        placement: 'right'
      },
      caretRenderer: () => null,
      "data-test-id": "insert-menu-VIDEO_INSERTION",
      buttonText: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(IconHolder, {
          marginInlineEnd: "300",
          icon: Icon,
          color: "icon-core-subtle"
        }), /*#__PURE__*/_jsx(Text, {
          color: disabled ? 'text-core-disabled' : 'text-core-default',
          children: buttonLabel
        }), showNewTag && /*#__PURE__*/_jsx(Badge, {
          use: "new",
          marginInlineStart: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "hubspot-prosemirror.insertMenu.newTag"
          })
        }), /*#__PURE__*/_jsx(IconHolder, {
          icon: RightIcon,
          color: "icon-core-default"
        })]
      }),
      Content: renderContent
    })
  });
};
const InsertDocumentsLockedButton = ({
  buttonLabel,
  Icon
}) => /*#__PURE__*/_jsx(Button, {
  "data-test-id": "insert-menu-DOCUMENTS-locked",
  onClick: () => {
    const portalId = PortalIdParser.get();
    if (portalId) {
      window.open(getDocumentsUrl(portalId), '_blank');
    }
  },
  children: /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(IconHolder, {
      marginInlineEnd: "300",
      icon: Icon,
      color: "icon-core-subtle"
    }), buttonLabel, /*#__PURE__*/_jsx(IconHolder, {
      icon: LockedIcon,
      color: "icon-core-subtle",
      marginInlineStart: "200",
      verticalAlign: "-0.25em",
      role: "img",
      "aria-label": "Locked"
    })]
  })
});
function InsertMenuTypeButton({
  type,
  editorConfig,
  onSelectMediaType,
  onOpenModal
}) {
  const config = editorConfig[type];
  const isUngatedForHubSpotVideoCapture = useIsUngatedForHubSpotVideoCapture();
  const hasOneToOneVideoAccess = useHasOneToOneVideoAccess();
  const hasMediaCaptureAccess = useHasMediaCaptureAccess();
  const hasDocumentsPaidAccessScope = useHasDocumentsPaidAccessScope();
  if (!config) {
    return null;
  }
  const buttonText = getTypeText(type, editorConfig);
  const disabledText = getDisabledText(type, editorConfig);
  const disabled = Boolean(disabledText);
  const Icon = getTypeIcon(type, editorConfig);
  const showNewTag = Boolean('showNewTag' in config && config.showNewTag);
  if (type === 'DOCUMENTS' && !hasDocumentsPaidAccessScope) {
    return /*#__PURE__*/_jsx(InsertDocumentsLockedButton, {
      buttonLabel: buttonText,
      Icon: Icon
    });
  }
  if (type === 'VIDEO_INSERTION') {
    const showDropdown = hasOneToOneVideoAccess || hasMediaCaptureAccess && isUngatedForHubSpotVideoCapture;
    return showDropdown ? /*#__PURE__*/_jsx(InsertVideoDropdownButton, {
      buttonLabel: buttonText,
      disabled: disabled,
      disabledText: disabledText,
      showNewTag: showNewTag,
      Icon: Icon,
      showCapture: hasMediaCaptureAccess && isUngatedForHubSpotVideoCapture,
      showVidyard: hasOneToOneVideoAccess,
      onSelectMediaType: onSelectMediaType,
      onOpenModal: onOpenModal
    }) : /*#__PURE__*/_jsx(InsertHubSpotVideoButton, {
      buttonLabel: buttonText,
      disabled: disabled,
      disabledText: disabledText,
      showNewTag: showNewTag,
      Icon: Icon,
      onSelectMediaType: onSelectMediaType,
      onOpenModal: onOpenModal,
      isInDropdown: false
    });
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !disabled,
    title: disabledText,
    children: /*#__PURE__*/_jsx(Button
    // className="p-x-4 display-flex align-center justify-between"
    , {
      disabled: disabled,
      onClick: () => onSelectMediaType(type),
      "data-test-id": `insert-menu-${type}`,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(IconHolder, {
          marginInlineEnd: "300",
          icon: Icon,
          color: "icon-core-subtle"
        }), buttonText, showNewTag && /*#__PURE__*/_jsx(Badge, {
          use: "new",
          marginInlineStart: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'hubspot-prosemirror.insertMenu.newTag'
          })
        }), /*#__PURE__*/_jsx(IconHolder, {
          icon: RightIcon
        })]
      })
    })
  });
}
export default InsertMenuTypeButton;